import { useMemo } from "react";

import { type AnalyticsColumnResult } from "../../models/audit/analyticsColumnResult";
import { useProcedureSessionTableShell } from "./useProcedureSessionTableShell";

interface UseCellSupersessionProps {
  procedureSessionId: string;
  /** The row the selection names, or nothing where it names none. */
  sourceRowId: string | undefined;
  /**
   * The verdict the surface holds — the one its header, provenance line and
   * conclusion describe. Absent before the selection resolves one, which is
   * nothing to compare against and so never superseded; an attribute-test
   * selection resolves none, so its surfaces pass `undefined` and are never
   * gated by this reader.
   */
  heldVerdict: AnalyticsColumnResult | undefined;
}

/**
 * Whether the cell this verdict was read on now holds a strictly later one.
 *
 * One reader for a fact two surfaces need: the analytics review sections and
 * the fold-out's deterministic-steps tab both frame the verdict the selection
 * resolved while reading the run beside it live for the cell, so each is one
 * account of one run only for as long as the cell still holds this verdict. A
 * second hand-written copy of the comparison would be two definitions of the
 * same fact, free to drift — one adopting strict-later, the other a bare
 * inequality — so both surfaces read the answer here.
 */
export function useCellSupersession({
  procedureSessionId,
  sourceRowId,
  heldVerdict,
}: UseCellSupersessionProps): boolean {
  // Which verdict this cell currently holds, asked of the table shell because
  // the shell is where the grid reads the same answer from: the sweep that
  // refreshes the live reads on these surfaces invalidates this key too, so the
  // two move together, and the work paper already holds it, so subscribing here
  // is a cache read rather than a request. The run ids on the verdict cannot
  // stand in for it — an override carries none.
  const { data: tableShell } =
    useProcedureSessionTableShell(procedureSessionId);

  const analyticsColumnId = heldVerdict?.analyticsColumnId;

  // The verdict the shell names for this cell, or nothing where it cannot name
  // one: a selection that names no row has no cell for the shell to answer
  // about, a shell that has not answered names nothing either, and a verdict
  // too malformed to parse is absent from the list rather than present under
  // its own id. Each of those leaves the surface exactly as it was, because the
  // notice is a claim and only a named successor supports it.
  //
  // Held against the whole row list for the same reason `documentNames` is: it
  // walks every sample of the session, and a render that changed neither the
  // shell nor the cell would otherwise walk them all again for the same answer.
  const currentVerdict = useMemo(
    () =>
      tableShell?.sourceRows
        .find((row) => row.id === sourceRowId)
        ?.analyticsResultsSummary.find(
          (summary) => summary.analyticsColumnId === analyticsColumnId,
        ),
    [tableShell?.sourceRows, sourceRowId, analyticsColumnId],
  );

  // Half of each surface is the verdict the caller resolved — its header,
  // provenance line and conclusion — and half is read live for the cell — its
  // steps and output files — so once the cell has moved on to a later verdict
  // the two halves describe different runs: a conclusion beside another run's
  // output files, and a fresh account of the new run's steps under the old
  // verdict's frame. In an audit that is misattributed evidence, so the surface
  // gives way to a notice rather than pair.
  //
  // Strictly later and not merely different, because the two reads refresh
  // independently and either can be the one that is behind: a saved override
  // points the pane at its successor before the shell's re-read lands, and a
  // bare inequality would meet the auditor with their own new conclusion
  // announced as superseded and the advice to reopen a cell that would take
  // them back to the row they just replaced. A date that will not parse
  // compares false, which leaves the surface as it was.
  //
  // `supersededAt` cannot stand in for this. It is the verdict's own answer to
  // whether something replaced it, and what these surfaces need to know is which
  // verdict the cell holds now — the question the live reads beside them were
  // answered for. Nor is the newest verdict by `createdAt` always the live one:
  // a re-judgment landing on a cell whose live row is a human override is
  // written already superseded so the override stays live, and the shell
  // projects the live row (`superseded_at IS NULL`), so comparing against what
  // the shell names — not against whatever is newest — stays correct there too.
  //
  // It answers for supersessions this client can see. Nothing here refetches on
  // its own — no read polls, and the socket renders toasts without touching the
  // cache — so where the newer run was started elsewhere the reads stay as they
  // were and there is nothing to compare.
  return (
    heldVerdict !== undefined &&
    currentVerdict !== undefined &&
    currentVerdict.id !== heldVerdict.id &&
    Date.parse(currentVerdict.createdAt) > Date.parse(heldVerdict.createdAt)
  );
}
